import { useRef, useState } from 'react';
import { Download, Upload, Loader2, CheckCircle2, AlertCircle, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { api, downloadBlob } from '@/api/client';

// Bulk imports/exports can take a while — override the client's default 30s timeout so big files aren't cut off.
const IMPORT_TIMEOUT_MS = 10 * 60 * 1000;

// Turn any upload failure into one plain-English sentence a non-technical user can act on.
function friendlyImportError(err: any): string {
  // Request never completed: axios timeout (file too big / too slow) or the connection dropped.
  if (err?.code === 'ECONNABORTED' || /timeout/i.test(String(err?.message ?? ''))) {
    return 'The file took too long and the upload stopped. Please split it into smaller files (about 500 rows each) and upload them one at a time.';
  }
  if (!err?.response) {
    return 'We could not reach the server. Please check your internet connection and try again.';
  }
  const status = err.response.status;
  const serverMsg = err.response.data?.message;
  if (status === 401 || status === 403) return 'Your login has expired. Please refresh the page, log in again, and upload the file once more.';
  if (status === 413) return 'This file is too large to upload. Please split it into smaller files (about 500 rows each).';
  if (serverMsg) return Array.isArray(serverMsg) ? serverMsg.join(' ') : String(serverMsg);
  if (status >= 500) return 'Something went wrong while saving. Please make sure your file matches the Sample CSV, then try again with fewer rows.';
  return 'The file could not be imported. Please click "Sample CSV", copy your data into it, save it, and upload again.';
}

interface Props {
  /** REST resource e.g. 'wms/brands' — Export hits GET /:resource/export, Import POSTs CSV to /:resource/import */
  resource: string;
  /** Optional query string appended to export URL e.g. 'merchantId=12' */
  exportQuery?: string;
  /** Optional sample CSV download URL */
  sampleUrl?: string;
  onImported?: () => void;
}

export function ExportImportBar({ resource, exportQuery = '', sampleUrl, onImported }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  type RowResult = { row: number; ref: string; status: string; message: string };
  const [result, setResult] = useState<{ inserted?: number; updated?: number; failed?: number; errors?: any[]; results?: RowResult[] } | null>(null);

  // Let the user download the full per-row status report (handy for big imports).
  const downloadReport = (rows: RowResult[]) => {
    const esc = (s: any) => { const v = String(s ?? ''); return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v; };
    const csv = ['Row,Item,Status,Message', ...rows.map((r) => [r.row, esc(r.ref), r.status, esc(r.message)].join(','))].join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `${fileBase}-import-report-${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
  };

  const fileBase = resource.replace(/\//g, '-');
  const onExport = async () => {
    const sep = exportQuery ? '?' : '';
    try { await downloadBlob(`/${resource}/export${sep}${exportQuery}`, `${fileBase}-${Date.now()}.csv`, { timeout: IMPORT_TIMEOUT_MS }); }
    catch (err: any) { toast.error(friendlyImportError(err)); }
  };

  // Sample/template CSV: column headers + an example row, so users know the import format.
  const onSample = async () => {
    if (sampleUrl) { window.open(sampleUrl, '_blank'); return; }
    try { await downloadBlob(`/${resource}/template`, `${fileBase}-sample.csv`); }
    catch (err: any) { toast.error(err?.response?.data?.message ?? 'Could not download the sample. Please try again.'); }
  };

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    // We can only read CSV files — guide Excel users before uploading instead of failing later.
    const name = f.name.toLowerCase();
    if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
      toast.error('Please upload a CSV file, not an Excel file. Click "Sample CSV", fill in your rows, then in Excel choose File → Save As → "CSV (Comma delimited)" and upload that file.');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await api.post(`/${resource}/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: IMPORT_TIMEOUT_MS });
      const data = r.data ?? { inserted: 0 };
      setResult(data);
      const ok = (data.inserted ?? 0) + (data.updated ?? 0);
      if (data.failed) {
        // Surface the first failure reason right in the toast; the full per-row list renders below.
        const first = data.errors?.[0];
        const hint = first ? ` — row ${first.row}: ${first.reason}` : '';
        toast.warning(`Imported ${ok} · ${data.failed} failed${hint}`);
      } else {
        toast.success(`Imported ${ok} rows`);
      }
      onImported?.();
    } catch (err: any) {
      toast.error(friendlyImportError(err), { duration: 8000 });
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const okCount = (result?.inserted ?? 0) + (result?.updated ?? 0);
  return (
    <div className="flex flex-col gap-2 items-end">
      <div className="flex items-center gap-2">
        <button onClick={onExport} className="px-2.5 py-1.5 text-xs border border-slate-300 rounded hover:bg-slate-50 flex items-center gap-1">
          <Download size={12} /> Export CSV
        </button>
        <button onClick={onSample} title="Download a sample CSV with the exact column headers + an example row"
          className="px-2.5 py-1.5 text-xs border border-indigo-300 text-indigo-700 rounded hover:bg-indigo-50 flex items-center gap-1">
          <FileText size={12} /> Sample CSV
        </button>
        <button onClick={() => fileRef.current?.click()} disabled={busy}
          className="px-2.5 py-1.5 text-xs border border-slate-300 rounded hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Import CSV
        </button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onUpload} />
        {result && (
          <span className={`text-xs flex items-center gap-1 ${result.failed ? 'text-amber-700' : 'text-emerald-700'}`}>
            {result.failed ? <AlertCircle size={11} /> : <CheckCircle2 size={11} />}
            {okCount} ok · {result.failed ?? 0} failed
          </span>
        )}
      </div>
      {/* Full per-row results: every row shown as uploaded / updated / error with its reason(s). */}
      {result?.results && result.results.length > 0 ? (
        <div className="w-full max-w-2xl border border-slate-300 bg-white rounded p-2 text-xs">
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium flex items-center gap-1">
              Import results — <span className="text-emerald-700">{okCount} ok</span> · <span className={result.failed ? 'text-rose-700' : 'text-slate-500'}>{result.failed ?? 0} error</span>
            </span>
            <div className="flex gap-3">
              <button onClick={() => downloadReport(result.results!)} className="text-indigo-600 hover:text-indigo-900 flex items-center gap-1"><Download size={11} /> Download report</button>
              <button onClick={() => setResult(null)} className="text-slate-500 hover:text-slate-900">Dismiss</button>
            </div>
          </div>
          <div className="max-h-60 overflow-auto border-t border-slate-100">
            <table className="w-full">
              <thead className="bg-slate-50 text-slate-500 sticky top-0">
                <tr><th className="text-left px-2 py-1 font-medium">#</th><th className="text-left px-2 py-1 font-medium">Item</th><th className="text-left px-2 py-1 font-medium">Status</th><th className="text-left px-2 py-1 font-medium">Message</th></tr>
              </thead>
              <tbody>
                {result.results.map((r) => (
                  <tr key={r.row} className={`border-t border-slate-100 ${r.status === 'error' ? 'bg-rose-50' : ''}`}>
                    <td className="px-2 py-1 text-slate-400">{r.row}</td>
                    <td className="px-2 py-1 font-mono">{r.ref}</td>
                    <td className="px-2 py-1">{r.status === 'error'
                      ? <span className="text-rose-700 font-medium">error</span>
                      : <span className="text-emerald-700 font-medium">{r.status}</span>}</td>
                    <td className="px-2 py-1 text-rose-700">{r.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : result?.errors && result.errors.length > 0 ? (
        <div className="w-full max-w-xl border border-amber-300 bg-amber-50 rounded p-2 text-xs text-amber-800">
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium flex items-center gap-1"><AlertCircle size={12} /> {result.failed} row{result.failed === 1 ? '' : 's'} failed</span>
            <button onClick={() => setResult(null)} className="text-amber-600 hover:text-amber-900">Dismiss</button>
          </div>
          <ul className="max-h-40 overflow-auto space-y-0.5 font-mono">
            {result.errors.map((e, i) => (
              <li key={i}>Row {e.row}: {e.reason}</li>
            ))}
          </ul>
          {result.failed != null && result.failed > result.errors.length && (
            <div className="mt-1 text-amber-600">…and {result.failed - result.errors.length} more (first {result.errors.length} shown)</div>
          )}
        </div>
      ) : null}
    </div>
  );
}
